/**
 * collection-filters-drawer.js
 * ---------------------------------------------------------------------------------------------------------
 * Live result count on the filter drawer's apply button — "View 32 products" rather than "Apply".
 *
 * WHY THIS IS CHEAPER THAN IT LOOKS
 *
 * Snow's FacetsForm already fetches the filtered result on every change. When the form is NOT in
 * update-on-change mode — which is exactly how the drawer is configured, since main-collection passes
 * update_on_change: false — onFormChanged calls cachedFetch(buildUrl()) purely to warm its cache so that
 * submitting feels instant. That response is a full section render, and it contains the count.
 *
 * The one thing it does not do is expose it. cachedFetch is module-scoped inside theme.js with no handle on
 * window, so this file repeats the request rather than reading Snow's cache. It keeps its own Map keyed by
 * URL, so toggling a filter off and back on costs nothing, and debounces so that running down a list of
 * checkboxes settles into a single request instead of one per click.
 *
 * WHY THE COUNT IS READ FROM MARKUP RATHER THAN COMPUTED
 *
 * The response already contains the count rendered through Shopify's own pluralisation and translation, in
 * collection-toolbar's .subheading. Lifting that string whole means no plural rules in JS, no locale table,
 * and no drift from what the toolbar itself will say once the filters are applied. Only the verb in front of
 * it is ours, and it arrives from Liquid as a data attribute so it stays translatable.
 * ---------------------------------------------------------------------------------------------------------
 */
(() => {
  if (window.__ossahFacetsCount) return;
  window.__ossahFacetsCount = true;

  const DRAWER = 'facets-drawer';
  const DEBOUNCE = 250;

  /**
   * Floor for the automatic single-line fit. Below this the label is small enough to be a legibility problem
   * of its own, and letting it wrap is the better failure — so the scale stops here rather than shrinking
   * without limit toward an unreadable button.
   */
  const MIN_FIT_SCALE = 0.55;

  /** Shaves a fraction off the computed fit so sub-pixel rounding cannot tip a just-fitting label onto a
   *  second line. */
  const FIT_SAFETY = 0.98;

  /** Where collection-toolbar renders the count. Same selector for collection and search. */
  const COUNT_SOURCE = '.collection__toolbar .subheading';

  const cache = new Map();

  /**
   * The current drawer's fit function. A facet update re-renders the whole section through the Section
   * Rendering API, which destroys the drawer and everything init closed over, so init runs again — and
   * anything bound to window inside it would accumulate one dead listener per filter change. Binding resize
   * once out here and calling through this handle keeps exactly one listener for the life of the page.
   */
  let currentFit = null;

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => currentFit?.(), 150);
  }, { passive: true });

  const init = () => {
    const drawer = document.querySelector(DRAWER);
    if (!drawer) return;

    const form = drawer.querySelector('form.facets');
    const button = drawer.querySelector('.facets-apply');
    if (!form || !button) return;

    const label = button.querySelector('.button__content');
    if (!label) return;

    const clearButton = drawer.querySelector('.facets-clear');
    const footer = button.closest('[slot="footer"]');

    /**
     * Both labels are wrapped in an inline span so their text can be measured directly. .button__content is a
     * flex container filling the button, so its own width says nothing about how wide the text is; an inline
     * box is exactly as wide as the glyphs it contains, with the uppercase transform and letter-spacing
     * already applied.
     *
     * replaceChildren rather than innerHTML. <button-content> is a custom element that throws when parsed
     * through innerHTML — its parentElement is null at that point — and while writing into an existing one
     * is safer than creating one, building the span as a node avoids the question entirely.
     */
    const textSpan = (host) => {
      let span = host.querySelector('.facets-fit-text');
      if (span) return span;

      span = document.createElement('span');
      span.className = 'facets-fit-text';
      span.textContent = host.textContent.trim();
      host.replaceChildren(span);
      return span;
    };

    /**
     * The verb, from Liquid. Falling back to the button's existing text means that if the attribute is ever
     * missing the button keeps saying whatever Snow put there rather than going blank.
     */
    const verb = drawer.getAttribute('data-view-label') || '';
    const fallback = label.textContent.trim();

    /**
     * buildUrl mirrors FacetsForm's private buildUrl_fn: current form state, plus section_id so Shopify
     * returns just this section. The three empty-value params it strips are the ones Snow strips, and for
     * the same reason — an empty price bound is not a filter, and sending it changes the URL without
     * changing the result, which would miss the cache on every keystroke.
     */
    const buildUrl = () => {
      const url = new URL(form.action, window.location.origin);
      const params = new URLSearchParams(new FormData(form));

      url.search = '';
      params.forEach((value, key) => url.searchParams.append(key, value));

      ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
        if (url.searchParams.get(key) === '') url.searchParams.delete(key);
      });

      const sectionId = form.closest('facets-form')?.getAttribute('section-id');
      if (sectionId) url.searchParams.set('section_id', sectionId);

      return url.toString();
    };

    /**
     * Parsed with DOMParser rather than a regex over the HTML. The count string is translated and
     * pluralised, so its shape varies by locale, and the only reliable way to find it is structurally.
     */
    const extractCount = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(COUNT_SOURCE)?.textContent.trim() || '';
    };

    /**
     * Measures both labels at their unscaled size and, if either overflows its button, writes a single scale
     * that brings the wider one back inside. Applied to the footer so both buttons take the same value.
     *
     * Returns early while the drawer is closed: it is display:none until shown, so every measurement would
     * be zero and would clamp the scale to its floor for no reason.
     */
    const fit = () => {
      if (!footer) return;

      footer.style.removeProperty('--facets-drawer-fit-scale');

      const measure = (host) => {
        if (!host) return 1;

        const span = host.querySelector('.facets-fit-text');
        if (!span) return 1;

        const style = getComputedStyle(host);
        const available =
          host.clientWidth - parseFloat(style.paddingInlineStart) - parseFloat(style.paddingInlineEnd);
        const needed = span.getBoundingClientRect().width;

        if (available <= 0 || needed <= 0) return 1;
        return available / needed;
      };

      const ratio = Math.min(measure(label), measure(clearButton?.querySelector('.button__content')));

      if (!Number.isFinite(ratio) || ratio >= 1) return;

      footer.style.setProperty(
        '--facets-drawer-fit-scale',
        String(Math.max(MIN_FIT_SCALE, ratio * FIT_SAFETY))
      );
    };

    const render = (countText) => {
      textSpan(label).textContent = countText ? `${verb} ${countText}`.trim() : fallback;
      fit();
    };

    // Wrapped up front so the Clear all label is measurable on the first fit, not only after a count change.
    if (clearButton) textSpan(clearButton.querySelector('.button__content'));

    let timer = null;
    let latest = 0;

    const refresh = () => {
      const url = buildUrl();

      if (cache.has(url)) {
        button.removeAttribute('data-count-pending');
        render(cache.get(url));
        return;
      }

      button.setAttribute('data-count-pending', '');

      /**
       * A monotonic token rather than an AbortController. Responses can land out of order when a slow
       * request is followed by a fast one, and the failure mode is a stale count sitting on the button with
       * no error to notice. Ignoring anything but the newest request is both simpler and safe to retry.
       */
      const token = ++latest;

      fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          const countText = extractCount(html);
          if (countText) cache.set(url, countText);
          if (token !== latest) return;
          button.removeAttribute('data-count-pending');
          render(countText);
        })
        .catch(() => {
          if (token !== latest) return;
          button.removeAttribute('data-count-pending');
          render('');
        });
    };

    form.addEventListener('change', () => {
      clearTimeout(timer);
      timer = setTimeout(refresh, DEBOUNCE);
    });

    /**
     * Reset clears the form asynchronously — the inputs are still checked when the event fires — so the
     * count is recalculated on the next frame, after the browser has actually cleared them.
     */
    form.addEventListener('reset', () => {
      clearTimeout(timer);
      requestAnimationFrame(refresh);
    });

    /**
     * Seeded on open rather than on load. The unfiltered count is already on the page, and fetching it
     * before the drawer has been opened would spend a request on a panel the visitor may never see.
     */
    drawer.addEventListener('dialog:before-show', () => {
      const current = document.querySelector(COUNT_SOURCE)?.textContent.trim();
      if (current) render(current);
    });

    /**
     * Measured again once the drawer is actually on screen. before-show fires while it is still display:none,
     * so the render above sets the text but cannot size it.
     */
    drawer.addEventListener('dialog:after-show', fit);

    // Published for the window-level resize handler, which is bound once rather than per init.
    currentFit = fit;
  };

  /**
   * Re-run after a facet update. Snow replaces the whole section via the Section Rendering API, which
   * destroys the form and the button this closed over, so the listeners have to be attached again. The
   * event is dispatched on the document by FacetsForm.
   */
  document.addEventListener('facet:update', () => requestAnimationFrame(init));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();